import type { FilterExpression, FilterGroup, UnifiedFilters } from 'types/filters';
import { isPropertyLeaf } from '@center-platform/spheres/filters';

export type ApplicabilityMode = 'all' | 'include' | 'exclude' | 'family';

export interface ClassifiedApplicability {
  mode: ApplicabilityMode;
  sphereTypes: string[];
  families?: string[];
}

// Properties that carry a schema system_name on a sphere. The platform uses
// `type` (concrete schema) and `label` (abstract schema family) — see
// ApplicabilitySection in the context editor for the canonical filter shape.
// `schemaSystemName` is recognized for compatibility with older payloads.
const SCHEMA_PROPERTIES = new Set(['type', 'label', 'schemaSystemName']);
const FAMILY_PROPERTIES = new Set(['family', 'sphereFamily', 'schemaFamily']);

const INCLUDE_OPERATORS = new Set<FilterExpression['operator']>(['equals', 'in']);
const EXCLUDE_OPERATORS = new Set<FilterExpression['operator']>(['not_equals', 'not_in']);

/**
 * Classify a `UnifiedFilters` payload into one of four plain-English applicability
 * modes used by the marketplace approach detail Overview.
 *
 *  - `all`     — no filters
 *  - `include` — equality / IN on `schemaSystemName`
 *  - `exclude` — negation / NOT IN on `schemaSystemName`
 *  - `family`  — equality on a sphere-family property (`family`, `sphereFamily`, `schemaFamily`)
 *
 * Compound or otherwise complex filters fall through to `include` with whatever
 * `schemaSystemName` values can be extracted; the consuming molecule is
 * responsible for rendering a generic "Available where: …" string in that case.
 *
 * Pure inspection — never throws, never mutates input.
 */
export function classifyApplicability(
  filters: UnifiedFilters | null | undefined,
): ClassifiedApplicability {
  if (!filters || !hasAnyFilter(filters)) {
    return { mode: 'all', sphereTypes: [] };
  }

  const propertyFilters = filters.propertyFilters ?? [];
  const filterGroups = filters.filterGroups ?? [];
  const perspectiveFilters = filters.perspectiveFilters ?? [];

  const isSingleExpression =
    propertyFilters.length === 1 &&
    filterGroups.length === 0 &&
    perspectiveFilters.length === 0;

  if (isSingleExpression) {
    const single = classifySingleExpression(propertyFilters[0]);
    if (single) return single;
  }

  return fallbackInclude(filters);
}

function hasAnyFilter(filters: UnifiedFilters): boolean {
  return (
    (filters.propertyFilters?.length ?? 0) > 0 ||
    (filters.perspectiveFilters?.length ?? 0) > 0 ||
    (filters.filterGroups?.length ?? 0) > 0
  );
}

function classifySingleExpression(
  expr: FilterExpression,
): ClassifiedApplicability | null {
  if (expr.type && expr.type !== 'property') return null;

  if (SCHEMA_PROPERTIES.has(expr.property)) {
    if (INCLUDE_OPERATORS.has(expr.operator)) {
      return { mode: 'include', sphereTypes: extractStringValues(expr) };
    }
    if (EXCLUDE_OPERATORS.has(expr.operator)) {
      return { mode: 'exclude', sphereTypes: extractStringValues(expr) };
    }
    return null;
  }

  if (FAMILY_PROPERTIES.has(expr.property) && INCLUDE_OPERATORS.has(expr.operator)) {
    return {
      mode: 'family',
      sphereTypes: [],
      families: extractStringValues(expr),
    };
  }

  return null;
}

function fallbackInclude(filters: UnifiedFilters): ClassifiedApplicability {
  const sphereTypes = collectSchemaSystemNames(filters);
  return { mode: 'include', sphereTypes };
}

function collectSchemaSystemNames(filters: UnifiedFilters): string[] {
  const collected = new Set<string>();

  for (const expr of filters.propertyFilters ?? []) {
    if (isSchemaIncludeExpression(expr)) {
      extractStringValues(expr).forEach((v) => collected.add(v));
    }
  }

  for (const group of filters.filterGroups ?? []) {
    walkGroup(group, collected);
  }

  return Array.from(collected);
}

function walkGroup(group: FilterGroup, collected: Set<string>): void {
  // A lane leaf (perspective/relationship/arc) never carries a schema
  // system_name — only property leaves are inspected here.
  for (const leaf of group.expressions ?? []) {
    if (isPropertyLeaf(leaf) && isSchemaIncludeExpression(leaf)) {
      extractStringValues(leaf).forEach((v) => collected.add(v));
    }
  }
  for (const nested of group.groups ?? []) {
    walkGroup(nested, collected);
  }
}

function isSchemaIncludeExpression(expr: FilterExpression): boolean {
  if (expr.type && expr.type !== 'property') return false;
  return SCHEMA_PROPERTIES.has(expr.property) && INCLUDE_OPERATORS.has(expr.operator);
}

function extractStringValues(expr: FilterExpression): string[] {
  if (Array.isArray(expr.values)) {
    return expr.values.filter((v): v is string => typeof v === 'string');
  }
  if (typeof expr.value === 'string') {
    return [expr.value];
  }
  if (Array.isArray(expr.value)) {
    return expr.value.filter((v): v is string => typeof v === 'string');
  }
  return [];
}
